// CB:BEGIN fr-014-realtor-dashboard
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiBaseUrl } from '../lib/getApiUrl';
import SharedHeaderRealtor from '../components/SharedHeaderRealtor';
import ContentLimiter from '../components/layout/ContentLimiter';
import { useDevice } from '../hooks/useDevice';
import { Plus, FileText, MapPin, Loader2, DollarSign, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import StatusBadge from '../components/ui/StatusBadge';

const BRAND_GREEN = '#123c3b';

interface QuoteRequest {
  id: string;
  propertyAddressLine1: string;
  propertyCity?: string | null;
  propertyState?: string | null;
  propertyZip?: string | null;
  status: string;
  bids: unknown[];
  createdAt?: string;
}

// CB:BEGIN fr-034-incentive-types
interface IncentiveLedgerRow {
  id: string;
  projectId: string;
  status: string;
  amountCents: number;
  currency: string;
  eligibleAt?: string | null;
  holdReleasesAt?: string | null;
  paidAt?: string | null;
  clawedBackAt?: string | null;
  clawbackReason?: string | null;
  project?: { id: string; address: string; status: string } | null;
  dispute?: { id: string; status: string } | null;
}
interface TaxProfile {
  w9Status: string;
  cumulativePaidCents: number;
  requires1099: boolean;
}
// CB:END fr-034-incentive-types

const INCENTIVE_STATUS_LABEL: Record<string, string> = {
  PENDING_CONVERSION: 'Pending conversion',
  CONVERTED: 'Converted',
  ELIGIBLE: 'Eligible for payout',
  APPROVED: 'Approved',
  PAID: 'Paid',
  CLAWED_BACK: 'Clawed back',
  VOID: 'Void',
};
const INCENTIVE_STATUS_COLOR: Record<string, string> = {
  PENDING_CONVERSION: '#94a3b8',
  CONVERTED: '#3b82f6',
  ELIGIBLE: '#f59e0b',
  APPROVED: '#8b5cf6',
  PAID: '#22c55e',
  CLAWED_BACK: '#ef4444',
  VOID: '#94a3b8',
};

const RealtorDashboard: React.FC<{ onNavigate: (screen: string) => void }> = ({ onNavigate }) => {
  const { getToken } = useAuth();
  const { isMobileLayout } = useDevice();
  const [requests, setRequests] = useState<QuoteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [address, setAddress] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressState, setAddressState] = useState('');
  const [addressZip, setAddressZip] = useState('');
  const [addressLat, setAddressLat] = useState<number | null>(null);
  const [addressLng, setAddressLng] = useState<number | null>(null);
  const addressInputRef = React.useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);

  // Google Places native autocomplete — matches ContractorSignup/PM pattern
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_API_KEY;
    if (!apiKey) return;

    const attachAutocomplete = () => {
      const inputEl = addressInputRef.current;
      if (!inputEl || !(window as any).google?.maps?.places) return;
      if ((inputEl as any).__gacAttached) return;
      (inputEl as any).__gacAttached = true;

      const ac = new (window as any).google.maps.places.Autocomplete(inputEl, {
        types: ['address'],
        fields: ['formatted_address', 'address_components', 'geometry'],
        componentRestrictions: { country: 'us' },
      });

      if (!document.getElementById('pac-style-overrides')) {
        const style = document.createElement('style');
        style.id = 'pac-style-overrides';
        style.textContent = `.pac-container { z-index: 9999 !important; } .pac-icon, .pac-icon-marker { display: none !important; } .pac-logo::after { display: none !important; } .pac-item { padding: 6px 12px; }`;
        document.head.appendChild(style);
      }

      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (place?.formatted_address) {
          if (addressInputRef.current) addressInputRef.current.value = place.formatted_address;
          setAddress(place.formatted_address);
        }
        // Parse structured address components
        if (place?.address_components) {
          const get = (type: string, nameType: 'long_name' | 'short_name' = 'long_name') => {
            const comp = place.address_components.find((c: any) => c.types.includes(type));
            return comp ? comp[nameType] : '';
          };
          setAddressCity(get('locality') || get('sublocality_level_1') || get('sublocality'));
          setAddressState(get('administrative_area_level_1', 'short_name'));
          setAddressZip(get('postal_code'));
        }
        if (place?.geometry?.location) {
          setAddressLat(place.geometry.location.lat());
          setAddressLng(place.geometry.location.lng());
        }
      });
    };

    const scriptId = 'google-places-script';
    if ((window as any).google?.maps?.places) {
      attachAutocomplete();
    } else if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=__cbPlacesReady`;
      script.async = true;
      (window as any).__cbPlacesReady = () => { attachAutocomplete(); delete (window as any).__cbPlacesReady; };
      document.head.appendChild(script);
    } else {
      const poll = setInterval(() => {
        if ((window as any).google?.maps?.places) { clearInterval(poll); attachAutocomplete(); }
      }, 100);
      setTimeout(() => clearInterval(poll), 10000);
    }

    return () => { if (addressInputRef.current) delete (addressInputRef.current as any).__gacAttached; };
  }, []);
  // CB:BEGIN fr-034-incentive-state
  const [activeTab, setActiveTab] = useState<'quotes' | 'incentives' | 'referrals'>('quotes');
  const [incentiveLedger, setIncentiveLedger] = useState<IncentiveLedgerRow[]>([]);
  const [taxProfile, setTaxProfile] = useState<TaxProfile | null>(null);
  const [incentivesLoading, setIncentivesLoading] = useState(false);
  const [disputingId, setDisputingId] = useState<string | null>(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputedIds, setDisputedIds] = useState<Set<string>>(new Set());
  // W-9 form state
  const [showW9Form, setShowW9Form] = useState(false);
  const [w9TinLast4, setW9TinLast4] = useState('');
  const [w9LegalName, setW9LegalName] = useState('');
  const [w9BusinessName, setW9BusinessName] = useState('');
  const [w9Submitting, setW9Submitting] = useState(false);
  const [w9SubmitSuccess, setW9SubmitSuccess] = useState(false);
  // Stripe Connect state
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripePayoutsEnabled, setStripePayoutsEnabled] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeJustConnected, setStripeJustConnected] = useState(false);
  // CB:BEGIN agreement-state
  const [agreementDownloading, setAgreementDownloading] = useState(false);
  const [agreementAccepting, setAgreementAccepting] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [myReferralLink, setMyReferralLink] = useState('');
  const [myReferralCode, setMyReferralCode] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  // CB:END agreement-state
  // Toast
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  // CB:BEGIN realtor-referrals-state
  const [referrals, setReferrals] = useState<any[]>([]);
  const [referralSummary, setReferralSummary] = useState<any>(null);
  const [referralsLoading, setReferralsLoading] = useState(false);
  // CB:END realtor-referrals-state
  // CB:END fr-034-incentive-state

  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const fetchQuotes = async () => {
    try {
      const token = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/quotes`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
      }
    } catch (e) {
      console.error('Fetch quotes failed', e);
    } finally {
      setLoading(false);
    }
  };

  // CB:BEGIN fr-034-fetch-incentives
  const fetchIncentives = useCallback(async () => {
    setIncentivesLoading(true);
    try {
      const token = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/incentives`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setIncentiveLedger(data.ledger || []);
        setTaxProfile(data.taxProfile || null);
      }
    } catch (e) {
      console.error('Fetch incentives failed', e);
    } finally {
      setIncentivesLoading(false);
    }
  }, [getToken]);

  const fetchStripeStatus = useCallback(async () => {
    try {
      const token = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/stripe/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setStripeConnected(!!data.connected);
        setStripePayoutsEnabled(!!data.payoutsEnabled);
      }
    } catch (e) {
      console.error('Fetch Stripe status failed', e);
    }
  }, [getToken]);

  // CB:BEGIN agreement-download
  const fetchAgreementStatus = useCallback(async () => {
    try {
      const token = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setAgreementAccepted(!!data.agreementAccepted);
        if (data.referralLink) setMyReferralLink(data.referralLink);
        if (data.referralCode) setMyReferralCode(data.referralCode);
      }
    } catch { /* non-fatal */ }
  }, [getToken]);

  const handleAgreementDownload = async () => {
    setAgreementDownloading(true);
    try {
      const token = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/agreement/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) {
          window.open(data.url, '_blank', 'noopener');
        } else {
          showToast('Download URL not available', false);
        }
      } else {
        showToast('Failed to get download link', false);
      }
    } catch {
      showToast('Failed to get download link', false);
    } finally {
      setAgreementDownloading(false);
    }
  };
  const handleAgreementAccept = async () => {
    setAgreementAccepting(true);
    try {
      const token = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/agreement/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (res.ok) {
        setAgreementAccepted(true);
        showToast('Agreement accepted — your referral link is now active.');
        fetchAgreementStatus();
      } else {
        const data = await res.json().catch(() => ({}));
        showToast(data.error || 'Failed to accept agreement', false);
      }
    } catch {
      showToast('Failed to accept agreement', false);
    } finally {
      setAgreementAccepting(false);
    }
  };
  // CB:END agreement-download

  const handleStripeConnect = async () => {
    setStripeLoading(true);
    try {
      const token = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/stripe/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        const err = await res.json().catch(() => ({}));
        showToast(String(err.error || 'Failed to start Stripe onboarding'), false);
      }
    } catch {
      showToast('Failed to start Stripe onboarding', false);
    } finally {
      setStripeLoading(false);
    }
  };

  const handleW9Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!w9TinLast4 || !w9LegalName.trim()) { showToast('TIN last 4 digits and legal name are required.', false); return; }
    if (!/^\d{4}$/.test(w9TinLast4)) { showToast('TIN last 4 must be exactly 4 digits.', false); return; }
    setW9Submitting(true);
    try {
      const token = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/w9`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ tinLast4: w9TinLast4, legalName: w9LegalName.trim(), businessName: w9BusinessName.trim() || undefined }),
      });
      if (res.ok) {
        setShowW9Form(false);
        setW9TinLast4(''); setW9LegalName(''); setW9BusinessName('');
        setW9SubmitSuccess(true);
        showToast('W-9 submitted for review ✓');
        fetchIncentives();
      } else {
        const err = await res.json().catch(() => ({}));
        showToast(String(err.error || 'Failed to submit W-9'), false);
      }
    } catch {
      showToast('Failed to submit W-9', false);
    } finally {
      setW9Submitting(false);
    }
  };

  const handleOpenDispute = async (ledgerId: string) => {
    if (!disputeReason.trim()) { showToast('Please provide a reason for the dispute.', false); return; }
    try {
      const token = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/incentives/${ledgerId}/dispute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ reason: disputeReason }),
      });
      if (res.ok) {
        setDisputingId(null);
        setDisputeReason('');
        setDisputedIds((prev) => new Set([...prev, ledgerId]));
        showToast('Dispute submitted — an admin will review your case ✓');
        fetchIncentives();
      } else {
        const err = await res.json().catch(() => ({}));
        showToast(String(err.error || 'Failed to submit dispute'), false);
      }
    } catch {
      showToast('Failed to submit dispute', false);
    }
  };
  // CB:END fr-034-fetch-incentives

  useEffect(() => {
    fetchQuotes();
    fetchAgreementStatus();
  }, [fetchAgreementStatus]);

  // CB:BEGIN realtor-referrals-fetch
  const fetchReferrals = useCallback(async () => {
    setReferralsLoading(true);
    try {
      const token = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/referrals`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setReferrals(data.referrals || []);
        setReferralSummary(data.summary || null);
      }
    } catch (e) {
      console.error('Fetch referrals failed', e);
    } finally {
      setReferralsLoading(false);
    }
  }, [getToken]);
  // CB:END realtor-referrals-fetch

  useEffect(() => {
    if (activeTab === 'incentives') {
      fetchIncentives();
      fetchStripeStatus();
      fetchAgreementStatus();
    }
    if (activeTab === 'referrals') {
      fetchReferrals();
    }
  }, [activeTab, fetchIncentives, fetchStripeStatus, fetchAgreementStatus, fetchReferrals]);

  // Handle return from Stripe onboarding
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'connected') {
      setActiveTab('incentives');
      setStripeJustConnected(true);
      fetchStripeStatus();
      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete('stripe');
      window.history.replaceState({}, '', url.toString());
    }
  }, [fetchStripeStatus]);

  const handleCreateQuote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.trim()) return;
    setSubmitting(true);
    try {
      const token = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          propertyAddressLine1: address.trim(),
          ...(addressCity && { propertyCity: addressCity }),
          ...(addressState && { propertyState: addressState }),
          ...(addressZip && { propertyZip: addressZip }),
          ...(addressLat !== null && { propertyLat: addressLat }),
          ...(addressLng !== null && { propertyLng: addressLng }),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setAddress('');
        setAddressCity(''); setAddressState(''); setAddressZip('');
        setAddressLat(null); setAddressLng(null);
        if (addressInputRef.current) addressInputRef.current.value = '';
        fetchQuotes();
        if (data.request?.id) onNavigate(`/realtor/quotes/${data.request.id}`);
      } else {
        const err = await res.json().catch(() => ({}));
        if (res.status === 403 && err.code === 'AGREEMENT_NOT_SIGNED') {
          setShowAgreementModal(true);
        } else {
          showToast(err.error || 'Failed to create quote request', false);
        }
      }
    } catch (e) {
      showToast('Failed to create quote request', false);
    } finally {
      setSubmitting(false);
    }
  };

  const formatAddress = (r: QuoteRequest) => {
    const parts = [r.propertyAddressLine1, r.propertyCity, r.propertyState, r.propertyZip].filter(Boolean);
    return parts.join(', ') || r.propertyAddressLine1;
  };

  const statusLabel = (s: string) => {
    const map: Record<string, string> = {
      COLLECTING_BIDS: 'Collecting bids',
      READY: 'Ready',
      EXPIRED: 'Expired',
      CONVERTED: 'Converted',
    };
    return map[s] || s;
  };

  return (
    <div style={{ fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, sans-serif', background: '#f8fafc', minHeight: '100vh' }}>
      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 20px', borderRadius: 10,
          background: toast.ok ? '#dcfce7' : '#fee2e2', color: toast.ok ? '#166534' : '#991b1b',
          fontWeight: 600, fontSize: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxWidth: 380,
        }}>
          {toast.msg}
        </div>
      )}

      {/* Agreement-required modal */}
      {showAgreementModal && (
        <div
          onClick={() => setShowAgreementModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 20, padding: 32, maxWidth: 400, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.2)', textAlign: 'center' }}
          >
            <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 10px' }}>Agreement required</h2>
            <p style={{ fontSize: 15, color: '#475569', margin: '0 0 24px', lineHeight: 1.5 }}>
              Sign the Referral Partner Agreement to submit quote requests for properties.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => setShowAgreementModal(false)}
                style={{ padding: '10px 20px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowAgreementModal(false); setActiveTab('incentives'); }}
                style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: BRAND_GREEN, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
              >
                Go to Agreement →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stripe connected success banner */}
      {stripeJustConnected && (
        <div style={{ background: '#dcfce7', borderBottom: '1px solid #bbf7d0', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CheckCircle size={20} color="#16a34a" />
            <span style={{ fontWeight: 700, color: '#14532d', fontSize: 15 }}>Stripe account connected successfully!</span>
            <span style={{ color: '#166534', fontSize: 14 }}>Payouts will be processed to your Stripe account automatically.</span>
          </div>
          <button onClick={() => setStripeJustConnected(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', fontSize: 18, lineHeight: 1, padding: 4 }}>✕</button>
        </div>
      )}

      <SharedHeaderRealtor onNavigate={onNavigate} />

      <section style={{ padding: isMobileLayout ? '24px 16px 48px' : '40px 24px 64px' }}>
        <ContentLimiter>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            {/* Page title */}
            <div style={{ marginBottom: 32 }}>
              <h1 style={{ fontSize: isMobileLayout ? 24 : 32, fontWeight: 800, color: '#0f172a', margin: 0 }}>
                Realtor Dashboard
              </h1>
            </div>

            {/* CB:BEGIN realtor-readiness-strip */}
            {/* Readiness strip — profile, agreement, referral link */}
            {(() => {
              const checks = [
                { label: 'Agreement signed', ok: agreementAccepted },
                { label: 'Referral link ready', ok: !!myReferralLink },
              ];
              const allDone = checks.every(c => c.ok);
              if (allDone) return null;
              return (
                <div style={{
                  background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: 12,
                  padding: '14px 20px', marginBottom: 24, display: 'flex', flexWrap: 'wrap',
                  alignItems: 'center', gap: 16,
                }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: '#92400e', flex: '0 0 auto' }}>Complete your setup:</span>
                  {checks.map(c => (
                    <span key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      {c.ok
                        ? <CheckCircle size={15} color="#16a34a" />
                        : <Clock size={15} color="#d97706" />}
                      <span style={{ color: c.ok ? '#166534' : '#92400e', fontWeight: c.ok ? 600 : 500 }}>{c.label}</span>
                    </span>
                  ))}
                  {!agreementAccepted && (
                    <button
                      onClick={() => setActiveTab('incentives')}
                      style={{ marginLeft: 'auto', padding: '6px 14px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    >
                      Go to Earnings & W-9 →
                    </button>
                  )}
                </div>
              );
            })()}
            {/* CB:END realtor-readiness-strip */}

            {/* CB:BEGIN fr-034-tabs */}
            {/* Tab bar — underline style */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 32, borderBottom: '1px solid #e2e8f0' }}>
              {([
                { id: 'quotes',     label: 'Quote Requests' },
                { id: 'referrals',  label: 'Referrals'      },
                { id: 'incentives', label: 'Earnings & W-9' },
              ] as const).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '10px 20px',
                    fontSize: 14,
                    fontWeight: activeTab === tab.id ? 700 : 500,
                    border: 'none',
                    borderBottom: activeTab === tab.id ? `2px solid ${BRAND_GREEN}` : '2px solid transparent',
                    background: 'none',
                    color: activeTab === tab.id ? BRAND_GREEN : '#64748b',
                    cursor: 'pointer',
                    marginBottom: -1,
                    transition: 'color 0.15s, border-color 0.15s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {/* CB:END fr-034-tabs */}

            {/* CB:BEGIN realtor-referrals-panel */}
            {activeTab === 'referrals' && (
              <div>
                {/* Your referral link */}
                <div style={{ background: '#fff', borderRadius: 16, border: '2px solid #e2e8f0', padding: isMobileLayout ? '20px 18px' : '24px 28px', marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke={BRAND_GREEN} strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>Your Referral Link</span>
                    {myReferralCode && (
                      <span style={{ marginLeft: 4, fontFamily: 'monospace', fontSize: 12, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 8px', color: '#475569', fontWeight: 600 }}>
                        {myReferralCode}
                      </span>
                    )}
                  </div>
                  {myReferralLink ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <input
                        readOnly
                        value={myReferralLink}
                        onClick={e => (e.target as HTMLInputElement).select()}
                        style={{ flex: '1 1 240px', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#374151', background: '#f8fafc', cursor: 'text', minWidth: 0, fontFamily: 'monospace' }}
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(myReferralLink).then(() => {
                            setLinkCopied(true);
                            setTimeout(() => setLinkCopied(false), 2000);
                          });
                        }}
                        style={{ padding: '10px 20px', background: linkCopied ? '#dcfce7' : BRAND_GREEN, color: linkCopied ? '#166534' : '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.2s' }}
                      >
                        {linkCopied ? '✓ Copied!' : 'Copy Link'}
                      </button>
                    </div>
                  ) : (
                    <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>
                      Accept the Referral Partner Agreement in the <button onClick={() => setActiveTab('incentives')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRAND_GREEN, fontWeight: 600, fontSize: 13, padding: 0 }}>Earnings & W-9</button> tab to generate your link.
                    </p>
                  )}
                  <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 10, marginBottom: 0 }}>
                    Share this link with homeowners. When they sign up and start a project, it's attributed to you.
                  </p>
                </div>

                {/* Summary cards */}
                {referralSummary && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 14, marginBottom: 28 }}>
                    {[
                      { label: 'Total Referrals', value: referralSummary.total, color: '#374151', bg: '#f8fafc' },
                      { label: 'Signed Up', value: referralSummary.signedUp, color: '#2563eb', bg: '#eff6ff' },
                      { label: 'Project Submitted', value: referralSummary.projectSubmitted, color: '#7c3aed', bg: '#f5f3ff' },
                      { label: 'Completed', value: referralSummary.projectCompleted, color: '#16a34a', bg: '#f0fdf4' },
                      { label: 'Paid Out', value: referralSummary.payoutPaid, color: '#0891b2', bg: '#ecfeff' },
                    ].map(c => (
                      <div key={c.label} style={{ background: c.bg, border: `1px solid ${c.color}25`, borderRadius: 12, padding: '16px 18px' }}>
                        <div style={{ fontSize: 28, fontWeight: 800, color: c.color }}>{c.value}</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>{c.label}</div>
                      </div>
                    ))}
                  </div>
                )}

                {referralsLoading ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading referrals…</div>
                ) : referrals.length === 0 ? (
                  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '48px 32px', textAlign: 'center' }}>
                    <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="#d1d5db" strokeWidth={1.5} style={{ display: 'block', margin: '0 auto 16px' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 8 }}>No referrals yet</div>
                    <div style={{ fontSize: 13, color: '#9ca3af', maxWidth: 320, margin: '0 auto' }}>Share your referral link with homeowners to track their progress here.</div>
                  </div>
                ) : (
                  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                            {['Referral Code','Status','Project','Payout','Date'].map(h => (
                              <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {referrals.map((r: any) => (
                            <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '12px 16px', fontFamily: 'monospace', color: '#374151', fontWeight: 600, fontSize: 13 }}>
                                {r.referralCode}
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                <StatusBadge status={r.status} label={r.status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase())} />
                              </td>
                              <td style={{ padding: '12px 16px', color: '#374151', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>
                                {r.project?.address || '—'}
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                <StatusBadge
                                  status={r.payoutStatus}
                                  label={r.payoutStatus === 'PAID' && r.payoutAmountCents
                                    ? `Paid ($${(r.payoutAmountCents / 100).toFixed(0)})`
                                    : r.payoutStatus}
                                />
                              </td>
                              <td style={{ padding: '12px 16px', color: '#94a3b8', whiteSpace: 'nowrap', fontSize: 12 }}>
                                {new Date(r.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* CB:END realtor-referrals-panel */}

            {activeTab === 'incentives' ? (
              /* CB:BEGIN fr-034-incentives-panel */
              <div>
                {/* CB:BEGIN realtor-agreement-card */}
                {/* Referral Agreement */}
                <div style={{ background: '#fff', borderRadius: 20, border: `2px solid ${agreementAccepted ? '#bbf7d0' : '#e2e8f0'}`, padding: isMobileLayout ? 20 : 28, marginBottom: 24 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <FileText size={20} color={agreementAccepted ? '#16a34a' : BRAND_GREEN} />
                      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>Referral Partner Agreement</h2>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {agreementAccepted && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 99, background: '#dcfce7', color: '#166534', fontSize: 13, fontWeight: 600 }}>
                          <CheckCircle size={14} /> Accepted
                        </span>
                      )}
                      <button
                        onClick={handleAgreementDownload}
                        disabled={agreementDownloading}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10, border: `2px solid ${BRAND_GREEN}`, background: '#fff', color: BRAND_GREEN, fontSize: 13, fontWeight: 600, cursor: agreementDownloading ? 'not-allowed' : 'pointer', opacity: agreementDownloading ? 0.7 : 1 }}
                      >
                        {agreementDownloading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <FileText size={14} />}
                        {agreementDownloading ? 'Loading…' : 'View / Download'}
                      </button>
                      {!agreementAccepted && (
                        <button
                          onClick={handleAgreementAccept}
                          disabled={agreementAccepting}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10, border: 'none', background: BRAND_GREEN, color: '#fff', fontSize: 13, fontWeight: 700, cursor: agreementAccepting ? 'not-allowed' : 'pointer', opacity: agreementAccepting ? 0.7 : 1 }}
                        >
                          {agreementAccepting ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={14} />}
                          {agreementAccepting ? 'Accepting…' : 'Accept Agreement'}
                        </button>
                      )}
                    </div>
                  </div>
                  <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>
                    {agreementAccepted
                      ? 'Your signed Realtor Referral Partner Agreement (Missouri) is on file. Download a copy for your records.'
                      : 'Accept the Realtor Referral Partner Agreement to activate your referral link and become eligible for the $150 referral fee.'}
                  </p>
                </div>
                {/* CB:END realtor-agreement-card */}

                {/* Tax profile status */}
                {/* Stripe Connect */}
                <div style={{ background: '#fff', borderRadius: 20, border: '2px solid #e2e8f0', padding: isMobileLayout ? 20 : 28, marginBottom: 24 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>Payout Account</h2>
                    {!stripeConnected ? (
                      <button
                        onClick={handleStripeConnect}
                        disabled={stripeLoading}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10, border: 'none', background: BRAND_GREEN, color: '#fff', fontSize: 14, fontWeight: 700, cursor: stripeLoading ? 'not-allowed' : 'pointer', opacity: stripeLoading ? 0.7 : 1 }}
                      >
                        {stripeLoading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                        {stripeLoading ? 'Redirecting…' : 'Connect Stripe'}
                      </button>
                    ) : (
                      <button
                        onClick={handleStripeConnect}
                        disabled={stripeLoading}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10, border: `2px solid ${BRAND_GREEN}`, background: '#fff', color: BRAND_GREEN, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                      >
                        Update Stripe account
                      </button>
                    )}
                  </div>
                  {stripeConnected ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CheckCircle size={18} color="#22c55e" />
                      <span style={{ fontSize: 14, color: '#166534', fontWeight: 600 }}>
                        Stripe connected{stripePayoutsEnabled ? ' · Payouts enabled' : ' · Completing setup…'}
                      </span>
                    </div>
                  ) : (
                    <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>
                      Connect a Stripe account to receive payout transfers directly. Required for automated payouts.
                    </p>
                  )}
                </div>

                {/* Tax Profile / W-9 */}
                <div style={{ background: '#fff', borderRadius: 20, border: '2px solid #e2e8f0', padding: isMobileLayout ? 20 : 28, marginBottom: 24 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>Tax Profile (W-9)</h2>
                    {(!taxProfile || taxProfile.w9Status === 'NOT_SUBMITTED' || taxProfile.w9Status === 'REJECTED') && (
                      <button
                        onClick={() => setShowW9Form((v) => !v)}
                        style={{ padding: '8px 16px', borderRadius: 10, border: `2px solid ${BRAND_GREEN}`, background: showW9Form ? BRAND_GREEN : '#fff', color: showW9Form ? '#fff' : BRAND_GREEN, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                      >
                        {showW9Form ? 'Cancel' : taxProfile?.w9Status === 'REJECTED' ? 'Resubmit W-9' : 'Submit W-9'}
                      </button>
                    )}
                  </div>

                  {taxProfile ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                      <div style={{ flex: '1 1 160px' }}>
                        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 4px' }}>W-9 Status</p>
                        <span style={{
                          display: 'inline-block', padding: '4px 12px', borderRadius: 9999, fontSize: 13, fontWeight: 700,
                          background: taxProfile.w9Status === 'VERIFIED' ? '#dcfce7' : taxProfile.w9Status === 'REJECTED' ? '#fee2e2' : taxProfile.w9Status === 'SUBMITTED_PENDING_REVIEW' ? '#dbeafe' : '#fef9c3',
                          color: taxProfile.w9Status === 'VERIFIED' ? '#166534' : taxProfile.w9Status === 'REJECTED' ? '#991b1b' : taxProfile.w9Status === 'SUBMITTED_PENDING_REVIEW' ? '#1e40af' : '#854d0e',
                        }}>
                          {taxProfile.w9Status === 'SUBMITTED_PENDING_REVIEW' ? 'Under Review' : String(taxProfile.w9Status || '').replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div style={{ flex: '1 1 160px' }}>
                        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 4px' }}>Cumulative paid</p>
                        <p style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                          ${(taxProfile.cumulativePaidCents / 100).toFixed(2)}
                        </p>
                      </div>
                      {taxProfile.requires1099 && taxProfile.w9Status !== 'VERIFIED' && (
                        <div style={{ flex: '1 1 100%', background: '#fef3c7', borderRadius: 10, padding: '10px 14px' }}>
                          <p style={{ fontSize: 14, color: '#92400e', margin: 0, fontWeight: 500 }}>
                            ⚠️ Your cumulative payouts exceed $600. A verified W-9 is required before your next payout can be processed.
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p style={{ color: '#64748b', margin: '0 0 4px' }}>No W-9 on file. Submit your W-9 information so payouts over $600 can be processed.</p>
                  )}

                  {w9SubmitSuccess && !showW9Form && taxProfile?.w9Status === 'SUBMITTED_PENDING_REVIEW' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe', marginTop: 12 }}>
                      <CheckCircle size={16} color="#3b82f6" />
                      <span style={{ fontSize: 14, color: '#1e40af', fontWeight: 600 }}>W-9 submitted — an admin will verify your information shortly.</span>
                    </div>
                  )}

                  {showW9Form && (
                    <form onSubmit={handleW9Submit} style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 440 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Legal name (as on tax return) *</label>
                        <input value={w9LegalName} onChange={(e) => setW9LegalName(e.target.value)} required placeholder="John Smith" style={{ width: '100%', padding: '10px 14px', fontSize: 14, border: '2px solid #e2e8f0', borderRadius: 10, boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Business name (optional)</label>
                        <input value={w9BusinessName} onChange={(e) => setW9BusinessName(e.target.value)} placeholder="ABC Realty LLC" style={{ width: '100%', padding: '10px 14px', fontSize: 14, border: '2px solid #e2e8f0', borderRadius: 10, boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>TIN last 4 digits (SSN or EIN) *</label>
                        <input value={w9TinLast4} onChange={(e) => setW9TinLast4(e.target.value.replace(/\D/g, '').slice(0, 4))} required placeholder="1234" maxLength={4} style={{ width: 100, padding: '10px 14px', fontSize: 14, border: '2px solid #e2e8f0', borderRadius: 10, letterSpacing: 4 }} />
                        <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 0 0' }}>We store only the last 4 digits for verification purposes.</p>
                      </div>
                      <button type="submit" disabled={w9Submitting} style={{ alignSelf: 'flex-start', padding: '10px 24px', borderRadius: 10, border: 'none', background: BRAND_GREEN, color: '#fff', fontSize: 14, fontWeight: 700, cursor: w9Submitting ? 'not-allowed' : 'pointer', opacity: w9Submitting ? 0.7 : 1 }}>
                        {w9Submitting ? 'Submitting…' : 'Submit W-9'}
                      </button>
                    </form>
                  )}
                </div>

                {/* Incentive ledger */}
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>
                  Incentive History
                </h2>
                {incentivesLoading ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                    <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', display: 'block', margin: '0 auto 10px' }} />
                    Loading…
                  </div>
                ) : incentiveLedger.length === 0 ? (
                  <div style={{ background: '#fff', borderRadius: 20, border: '2px solid #e2e8f0', padding: 40, textAlign: 'center' }}>
                    <DollarSign size={36} style={{ display: 'block', margin: '0 auto 12px', color: '#cbd5e1' }} />
                    <p style={{ fontWeight: 700, color: '#0f172a', fontSize: 15, margin: '0 0 8px' }}>No incentive records yet</p>
                    <p style={{ color: '#64748b', fontSize: 14, margin: 0, maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
                      Incentives are earned when a homeowner you referred submits a quote request and it converts to an active project. Create a quote request above to get started.
                    </p>
                  </div>
                ) : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {incentiveLedger.map((row) => (
                      <li key={row.id} style={{ background: '#fff', borderRadius: 16, border: '2px solid #e2e8f0', padding: isMobileLayout ? 18 : 24 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                          <div style={{ flex: '1 1 220px' }}>
                            <p style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', margin: '0 0 4px' }}>
                              {row.project?.address || row.projectId}
                            </p>
                            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 8px' }}>
                              Amount: <strong>${(row.amountCents / 100).toFixed(2)}</strong>
                              {row.eligibleAt && ` · Eligible: ${new Date(row.eligibleAt).toLocaleDateString()}`}
                              {row.holdReleasesAt && row.status === 'ELIGIBLE' && (() => {
                                const daysLeft = Math.ceil((new Date(row.holdReleasesAt).getTime() - Date.now()) / 86_400_000);
                                return daysLeft > 0
                                  ? ` · Hold: ${daysLeft}d remaining`
                                  : ` · Hold period elapsed`;
                              })()}
                              {row.paidAt && ` · Paid: ${new Date(row.paidAt).toLocaleDateString()}`}
                              {row.clawedBackAt && ` · Clawed back: ${new Date(row.clawedBackAt).toLocaleDateString()}`}
                            </p>
                            {row.clawbackReason && (
                              <p style={{ fontSize: 12, color: '#dc2626', margin: '0 0 8px', background: '#fef2f2', padding: '4px 8px', borderRadius: 6 }}>
                                Reason: {String(row.clawbackReason)}
                              </p>
                            )}
                            <span style={{
                              display: 'inline-block', padding: '3px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 700,
                              background: INCENTIVE_STATUS_COLOR[row.status] + '22',
                              color: INCENTIVE_STATUS_COLOR[row.status],
                            }}>
                              {INCENTIVE_STATUS_LABEL[row.status] || row.status}
                            </span>
                          </div>
                          {row.status === 'CLAWED_BACK' && (
                            <div>
                              {(disputedIds.has(row.id) || row.dispute) ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                                  <Clock size={14} color="#3b82f6" />
                                  <span style={{ fontSize: 13, color: '#1e40af', fontWeight: 600 }}>
                                    Dispute submitted — under review
                                  </span>
                                </div>
                              ) : disputingId === row.id ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 280 }}>
                                  <textarea
                                    value={disputeReason}
                                    onChange={(e) => setDisputeReason(e.target.value)}
                                    placeholder="Describe why this clawback should be reversed…"
                                    rows={3}
                                    style={{ padding: 10, borderRadius: 8, border: '2px solid #e2e8f0', fontSize: 14, resize: 'vertical' }}
                                  />
                                  <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                      onClick={() => handleOpenDispute(row.id)}
                                      style={{ flex: 1, padding: '8px 14px', borderRadius: 8, border: 'none', background: BRAND_GREEN, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                                    >
                                      Submit dispute
                                    </button>
                                    <button
                                      onClick={() => { setDisputingId(null); setDisputeReason(''); }}
                                      style={{ padding: '8px 14px', borderRadius: 8, border: '2px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 14, cursor: 'pointer' }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setDisputingId(row.id)}
                                  style={{ padding: '8px 16px', borderRadius: 8, border: '2px solid #ef4444', background: '#fff', color: '#ef4444', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                                >
                                  <AlertTriangle size={14} style={{ display: 'inline', marginRight: 6 }} />
                                  Dispute this clawback
                                </button>
                              )}
                            </div>
                          )}
                          {row.status === 'PAID' && (
                            <CheckCircle size={22} color="#22c55e" />
                          )}
                          {row.status === 'ELIGIBLE' && (
                            <Clock size={22} color="#f59e0b" title="Hold period in progress" />
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div>
            {/* New quote request card */}
            <div
              style={{
                background: '#fff',
                borderRadius: 20,
                border: '2px solid #e2e8f0',
                padding: isMobileLayout ? 24 : 32,
                marginBottom: 32,
                boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
              }}
            >
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
                New quote request
              </h2>
              <p style={{ fontSize: 15, color: '#64748b', marginBottom: 20, lineHeight: 1.5 }}>
                Enter a property address to request up to 3 contractor bids for roof replacement.
              </p>
              <form onSubmit={handleCreateQuote}>
                <label htmlFor="realtor-address" style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#334155', marginBottom: 8 }}>
                  Property address
                </label>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <input
                    ref={addressInputRef}
                    id="realtor-address"
                    type="text"
                    defaultValue={address}
                    onChange={(e) => {
                      setAddress(e.target.value);
                      // Clear parsed components if user edits manually
                      setAddressCity(''); setAddressState(''); setAddressZip('');
                      setAddressLat(null); setAddressLng(null);
                    }}
                    placeholder="123 Main St, City, ST ZIP"
                    autoComplete="off"
                    style={{
                      flex: '1 1 260px',
                      padding: '14px 18px',
                      fontSize: 16,
                      border: '2px solid #e2e8f0',
                      borderRadius: 12,
                      outline: 'none',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = BRAND_GREEN; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '14px 24px',
                      fontSize: 16,
                      fontWeight: 700,
                      color: '#fff',
                      background: BRAND_GREEN,
                      border: 'none',
                      borderRadius: 12,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      opacity: submitting ? 0.7 : 1,
                      boxShadow: '0 4px 14px rgba(18,60,59,0.25)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {submitting ? <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={20} />}
                    {submitting ? 'Requesting…' : 'Request quotes'}
                  </button>
                </div>
              </form>
            </div>

            {/* Your quote requests */}
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>
              Your quote requests
            </h2>

            {loading ? (
              <div
                style={{
                  background: '#fff',
                  borderRadius: 20,
                  border: '2px solid #e2e8f0',
                  padding: 48,
                  textAlign: 'center',
                  color: '#64748b',
                  fontSize: 16,
                }}
              >
                <Loader2 size={32} style={{ margin: '0 auto 12px', display: 'block', animation: 'spin 1s linear infinite' }} />
                Loading quote requests…
              </div>
            ) : requests.length === 0 ? (
              <div
                style={{
                  background: '#fff',
                  borderRadius: 20,
                  border: '2px solid #e2e8f0',
                  padding: 48,
                  textAlign: 'center',
                  color: '#64748b',
                  fontSize: 16,
                }}
              >
                <MapPin size={40} style={{ margin: '0 auto 16px', display: 'block', color: '#cbd5e1' }} />
                No quote requests yet. Create one above to get up to 3 contractor bids for a property.
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {requests.map((r) => (
                  <li
                    key={r.id}
                    style={{
                      background: '#fff',
                      borderRadius: 20,
                      border: '2px solid #e2e8f0',
                      padding: isMobileLayout ? 20 : 24,
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 16,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = BRAND_GREEN;
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(18,60,59,0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.04)';
                    }}
                  >
                    <div style={{ flex: '1 1 200px' }}>
                      <p style={{ fontSize: 17, fontWeight: 600, color: '#0f172a', margin: '0 0 6px 0', lineHeight: 1.4 }}>
                        {formatAddress(r)}
                      </p>
                      <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 4px' }}>
                        {statusLabel(r.status)} · {Array.isArray(r.bids) ? r.bids.length : 0} bid{r.bids?.length === 1 ? '' : 's'}
                      </p>
                      {r.status === 'READY' && (
                        <p style={{ fontSize: 13, color: BRAND_GREEN, fontWeight: 600, margin: 0 }}>
                          ✓ Bids ready — share with your client
                        </p>
                      )}
                      {r.status === 'COLLECTING_BIDS' && (
                        <p style={{ fontSize: 13, color: '#f59e0b', fontWeight: 600, margin: 0 }}>
                          ⏳ Collecting bids — check back within 24–48 hrs
                        </p>
                      )}
                      {r.status === 'CONVERTED' && (
                        <p style={{ fontSize: 13, color: '#8b5cf6', fontWeight: 600, margin: 0 }}>
                          🎉 Converted — project is active
                        </p>
                      )}
                      {r.status === 'EXPIRED' && (
                        <p style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600, margin: 0 }}>
                          Expired — no further action needed
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => onNavigate(`/realtor/quotes/${r.id}`)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '12px 20px',
                        fontSize: 15,
                        fontWeight: 600,
                        color: BRAND_GREEN,
                        background: '#fff',
                        border: `2px solid ${BRAND_GREEN}`,
                        borderRadius: 12,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <FileText size={18} />
                      View
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
            )}
          </div>
        </ContentLimiter>
      </section>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default RealtorDashboard;
// CB:END fr-014-realtor-dashboard
